import { useEffect, useRef, useState } from "react";
import Spinner from "@cloudscape-design/components/spinner";
import { NotesTaker } from "../components/notes/NotesTaker";
import { useAppContext } from "../contexts/AppContext";
import {
  NOTES_POPOUT_STOP_MESSAGE,
  NOTES_POPOUT_STOPPED_CONTACT_KEY,
  getRequestedPopoutContactId,
  hydrateNotesPopoutSession,
  isC360StateMessage,
  isNotesPopoutBootstrapMessage,
  sendC360SearchToParent,
  signalNotesPopoutReady,
  type C360StatePayload,
} from "../lib/notesPopout";

const styles: Record<string, React.CSSProperties> = {
  container: {
    fontFamily: "system-ui, -apple-system, sans-serif",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#ffffff",
  },
  content: {
    flex: 1,
    overflow: "hidden",
  },
  statusMessage: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "16px",
    color: "#6b7280",
    padding: "24px",
    textAlign: "center",
  },
  errorText: {
    fontSize: "14px",
    color: "#ef4444",
    maxWidth: "420px",
  },
};

export default function NotesPopoutPage() {
  const { refreshFeatures, setAgentArnFromExternal } = useAppContext();
  const [contactId, setContactId] = useState<string | null>(() => getRequestedPopoutContactId());
  const [ready, setReady] = useState<boolean>(() => {
    return Boolean(getRequestedPopoutContactId() && sessionStorage.getItem("notetaker_token"));
  });
  const parentWindowRef = useRef<MessageEventSource | null>(window.opener);
  const [stoppedContactId, setStoppedContactId] = useState<string | null>(
    () => sessionStorage.getItem(NOTES_POPOUT_STOPPED_CONTACT_KEY),
  );

  useEffect(() => {
    const handleStop = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== parentWindowRef.current) return;
      if (!contactId || event.data?.type !== NOTES_POPOUT_STOP_MESSAGE || event.data.contactId !== contactId) return;
      sessionStorage.setItem(NOTES_POPOUT_STOPPED_CONTACT_KEY, contactId);
      setStoppedContactId(contactId);
    };
    window.addEventListener("message", handleStop);
    return () => window.removeEventListener("message", handleStop);
  }, [contactId]);

  const [error, setError] = useState<string | null>(null);
  const [externalC360, setExternalC360] = useState<Omit<C360StatePayload, "type"> | null>(null);

  // Listen for C360 state forwarded from the parent window.
  useEffect(() => {
    const handleC360 = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (isC360StateMessage(event.data)) {
        setExternalC360(event.data);
      }
    };
    window.addEventListener("message", handleC360);
    return () => window.removeEventListener("message", handleC360);
  }, []);

  // Always listen for bootstrap messages — even when we already have the
  // token in sessionStorage. The parent sends a fresh bootstrap (with the
  // agent ARN for the allowlist check) every time the popout signals
  // ready, and we need to hydrate that ARN into AppContext regardless of
  // which path produced the initial token.
  useEffect(() => {
    const handleBootstrap = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isNotesPopoutBootstrapMessage(event.data)) return;
      // A named popup may be reused by a different contact app iframe.
      parentWindowRef.current = event.source;
      if (event.data.agentArn) setAgentArnFromExternal(event.data.agentArn);
    };
    window.addEventListener("message", handleBootstrap);
    return () => window.removeEventListener("message", handleBootstrap);
  }, [setAgentArnFromExternal]);

  useEffect(() => {
    const requestedContactId = getRequestedPopoutContactId();
    if (requestedContactId) {
      setContactId(requestedContactId);
    }
  }, []);

  useEffect(() => {
    const tokenAvailable = Boolean(sessionStorage.getItem("notetaker_token"));
    if (contactId && tokenAvailable) {
      refreshFeatures();
      setReady(true);
      signalNotesPopoutReady();
      return;
    }

    signalNotesPopoutReady();

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (!isNotesPopoutBootstrapMessage(event.data)) {
        return;
      }

      hydrateNotesPopoutSession(event.data.contactId, event.data.token);
      setContactId(event.data.contactId);
      if (event.data.agentArn) setAgentArnFromExternal(event.data.agentArn);
      refreshFeatures();
      setError(null);
      setReady(true);
    };

    window.addEventListener("message", handleMessage);

    const timeoutId = window.setTimeout(() => {
      if (!sessionStorage.getItem("notetaker_token")) {
        setError("Open the notepad from Amazon Connect so this window can receive the active session.");
      }
    }, 3000);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearTimeout(timeoutId);
    };
  }, [contactId]);

  if (!ready || !contactId) {
    return (
      <div style={styles.container}>
        <div style={styles.statusMessage}>
          {error ? (
            <>
              <p>
                <strong>Notepad unavailable</strong>
              </p>
              <p style={styles.errorText}>{error}</p>
            </>
          ) : (
            <>
              <Spinner />
              <p>Waiting for Amazon Connect to open this notepad...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <NotesTaker streamingEnabled={stoppedContactId !== contactId} contactId={contactId} showPopoutButton={false} externalC360={externalC360} onC360Search={sendC360SearchToParent} />
      </div>
    </div>
  );
}
